<template>
  <div :id="containerId" class="pdf-container">
    <PdfReader class="pdf-reader-root" :src="pdfFile" />
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import PdfReader from './PdfReader.vue'

const KIND = 'bopus-pdf-viewer'

// NOTE(pspdfkit-to-bopus): Keep this file JS-only for Vue CLI 4 compat.
// We intentionally avoid TS declarations here.

const props = defineProps({
  /**
   * URL (or blob URL) to the PDF to render.
   */
  pdfFile: {
    type: String,
    required: true
  },

  /**
   * Optional stable key used by some callers to form DOM ids.
   */
  keyIndex: {
    type: String,
    required: false
  }
})

const emit = defineEmits([
  /**
   * Purpose:
   * - Signals that the viewer has been mounted and is ready for use.
   *
   * Returns:
   * - void (event)
   * - Payload is a lightweight handle (NOT a PSPDFKit instance).
   *
   * Parameters:
   * - payload (LoadedPayload): viewer handle for callers that expect an instance-like value.
   */
  'loaded'
])

/*
Purpose:
- Builds the DOM id for the wrapper container, mirroring the previous PSPDFKitContainer behavior.

Returns:
- string: container element id used for targeting/styling.
- Edge cases: if keyIndex is missing, defaults to 'default'.

Parameters:
- keyIndex (string | undefined): stable identifier provided by callers.
*/
function buildContainerId(keyIndex) {
  return `pdf-container-${keyIndex ?? 'default'}`
}

const containerId = computed(() => buildContainerId(props.keyIndex))

onMounted(() => {
  // NOTE:
  // - PSPDFKitContainer emitted a PSPDFKit instance.
  // - bopus viewer does not expose an equivalent instance API today.
  // We emit a lightweight handle so callers that store the instance don’t crash.
  emit('loaded', {
    kind: KIND,
    version: 1,
    pdfFile: props.pdfFile,
    keyIndex: props.keyIndex
  })
})
</script>

<style scoped lang="scss">
.pdf-container {
  max-width: 100%;
  height: 100%;
  margin: 0 auto;
  /* Matches the historical PSPDFKit container sizing assumption (portrait pages). */
  aspect-ratio: 1 / 1.41;
}

.pdf-reader-root {
  height: 100%;
}
</style>
